import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link as MuiLink,
  Typography
} from '@mui/material'
import GoogleIcon from '@mui/icons-material/Google'
import { useNavigate, Link } from 'react-router'
import { getFirestore, doc, getDoc } from 'firebase/firestore'
import type { User as FirebaseUser, UserCredential } from 'firebase/auth'
import AppBar from '../components/AppBar'
import Wrapper from '../components/Wrapper'
import EmailPasswordForm from '../components/EmailPasswordForm'
import routes from './routes'
import { signInWithGoogle } from '../services/authService'
import { createUserDocument } from '../services/profileService'
import { writeSignupAcknowledgement } from '../services/acknowledgementService'
import { useSessionStore } from '../state'
import { serializeProfile } from '../utils/serializeProfile'

const Signin = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [showTermsDialog, setShowTermsDialog] = useState(false)
  const [pendingGoogleResult, setPendingGoogleResult] =
    useState<UserCredential | null>(null)
  const navigate = useNavigate()
  const { setSession, setProfile } = useSessionStore()

  const handleEmailSignInSuccess = async (
    user: FirebaseUser,
    isNewUser: boolean
  ) => {
    const userDoc = await getDoc(doc(getFirestore(), 'users', user.uid))
    if (userDoc.exists()) {
      const profile = serializeProfile(userDoc.id, userDoc.data())
      setSession({
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL ?? null,
          emailVerified: user.emailVerified,
          providerId: user.providerData[0]?.providerId ?? 'password'
        },
        isAuthenticating: false
      })
      setProfile(profile)
      if (profile.profileComplete) {
        navigate(routes.home)
      } else {
        navigate(routes.completeProfile)
      }
    } else {
      navigate(routes.completeProfile)
    }
  }

  const handleVerificationRequired = () => {
    navigate(routes.verifyEmail)
  }

  const handleSignIn = async () => {
    setLoading(true)
    setError(null)

    try {
      const result = await signInWithGoogle()
      const { uid, email, displayName } = result.user

      const userDoc = await getDoc(doc(getFirestore(), 'users', uid))

      if (userDoc.exists()) {
        const profile = serializeProfile(userDoc.id, userDoc.data())
        setSession({
          user: {
            uid,
            email,
            displayName,
            photoURL: result.user.photoURL ?? null,
            emailVerified: result.user.emailVerified,
            providerId: result.user.providerData[0]?.providerId ?? 'google.com'
          },
          isAuthenticating: false
        })
        setProfile(profile)
        navigate(routes.home)
      } else {
        // New user — show T&C dialog before completing account creation
        setPendingGoogleResult(result)
        setShowTermsDialog(true)
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Sign in failed. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleTermsDialogContinue = async () => {
    if (!pendingGoogleResult) return

    setLoading(true)
    setError(null)

    try {
      const { uid, email, displayName } = pendingGoogleResult.user

      await createUserDocument(uid, email ?? '', displayName ?? '')
      await writeSignupAcknowledgement(uid, email ?? '')

      setSession({
        user: {
          uid,
          email,
          displayName,
          photoURL: pendingGoogleResult.user.photoURL ?? null,
          emailVerified: pendingGoogleResult.user.emailVerified,
          providerId:
            pendingGoogleResult.user.providerData[0]?.providerId ?? 'google.com'
        },
        isAuthenticating: false
      })
      setProfile({
        id: uid,
        displayName: displayName ?? '',
        email: email ?? '',
        handle: null,
        suburb: null,
        location: null,
        role: 'member',
        guidedDiveFlag: false,
        profileComplete: false,
        creditBalance: 0,
        accreditation: null,
        bankDetails: null,
        allowlist: null,
        perfectRatingCount: null,
        ratingAggregates: null,
        accountStatus: 'active',
        createdAt: new Date(),
        updatedAt: new Date()
      })

      setShowTermsDialog(false)
      setPendingGoogleResult(null)
      setTermsAccepted(false)
      navigate(routes.completeProfile)
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Account creation failed. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <AppBar title='Sign In' linkBackTo={routes.welcome} />
      <Wrapper>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          mt={8}
          gap={3}
        >
          {error && (
            <Alert severity='error' sx={{ width: '100%', maxWidth: 400 }}>
              {error}
            </Alert>
          )}
          <EmailPasswordForm
            onSignInSuccess={handleEmailSignInSuccess}
            onVerificationRequired={handleVerificationRequired}
          />
          <Divider sx={{ width: '100%', maxWidth: 400 }}>or</Divider>
          <Button
            variant='contained'
            size='large'
            startIcon={
              loading ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                <GoogleIcon />
              )
            }
            onClick={handleSignIn}
            disabled={loading}
            sx={{ textTransform: 'none', minWidth: 240 }}
          >
            {loading ? 'Signing in...' : 'Sign in with Google'}
          </Button>
          <MuiLink
            component={Link}
            to={routes.terms}
            variant='body2'
            sx={{ mt: 2 }}
          >
            Terms & Conditions
          </MuiLink>
        </Box>
      </Wrapper>

      <Dialog open={showTermsDialog} onClose={() => {}}>
        <DialogTitle>Accept Terms & Conditions</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            To create your account, please review and accept our Terms &
            Conditions.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={e => setTermsAccepted(e.target.checked)}
              />
            }
            label={
              <Typography variant='body2'>
                I accept the{' '}
                <MuiLink component={Link} to={routes.terms} target='_blank'>
                  Terms & Conditions
                </MuiLink>
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowTermsDialog(false)
              setPendingGoogleResult(null)
              setTermsAccepted(false)
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleTermsDialogContinue}
            disabled={!termsAccepted || loading}
          >
            {loading ? 'Creating account...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default Signin
